import * as React from 'react';
import { useEffect, useState } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useAppContext } from '@app/react-common/AppContext';

export interface PrioritizableItem {
  id: number;
  name: string;
  priority?: number;
}

interface PrioritizationModalProps<T extends PrioritizableItem> {
  items: T[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (orderedItems: T[]) => Promise<void>;
  title: string;
  infoText?: string;
  lockFirstItem?: boolean;
  renderItemDetails?: (item: T, index: number) => React.ReactNode;
  successMessage?: string;
  errorMessage?: string;
}

export function PrioritizationModal<T extends PrioritizableItem>({
  items,
  isOpen,
  onClose,
  onSave,
  title,
  infoText,
  lockFirstItem = false,
  renderItemDetails,
  successMessage = 'Prioritization saved',
  errorMessage = 'Failed to update prioritization',
}: PrioritizationModalProps<T>) {
  const { showToast } = useAppContext();
  const [orderedItems, setOrderedItems] = useState<T[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (items && items.length > 0) {
      // Deep clone to avoid read-only issues from Immer or frozen state
      setOrderedItems(items.map((item) => ({ ...item })));
    }
  }, [items, isOpen]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (lockFirstItem && result.destination.index === 0) {
      return;
    }

    if (lockFirstItem && result.source.index === 0) {
      return;
    }

    // Create a new array with cloned items to avoid mutation issues
    const reorderedItems = orderedItems.map((item) => ({ ...item }));
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);

    setOrderedItems(reorderedItems);
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(orderedItems);
      showToast({ message: successMessage, mode: 'info' });
      onClose();
    } catch (error) {
      showToast({ message: errorMessage, mode: 'danger' });
    } finally {
      setIsSaving(false);
    }
  };

  const isDraggable = (index: number) => {
    if (!lockFirstItem) return true;
    return index !== 0;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="prioritization-modal">
        <h2 className="prioritization-modal__title">{title}</h2>
        {infoText && (
          <div className="prioritization-modal__info-box">
            <i className="icon icon-more_information" />
            {infoText}
          </div>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="prioritization-list">
            {(provided) => (
              <div className="prioritization-modal__list" {...provided.droppableProps} ref={provided.innerRef}>
                {orderedItems.map((item, index) => {
                  if (!isDraggable(index)) {
                    return (
                      <div key={item.id.toString()} className="prioritization-modal__list__item-wrapper">
                        <span className="prioritization-modal__list__item__number">{index + 1}</span>
                        <div className="prioritization-modal__list__item disabled">
                          <div className="prioritization-modal__list__item__content">
                            <span className="prioritization-modal__list__item__name">{item.name}</span>
                            {renderItemDetails && (
                              <div className="prioritization-modal__list__item__right-content">
                                {renderItemDetails(item, index)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index}>
                      {(provided, snapshot) => (
                        <div
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          className={`prioritization-modal__list__item-wrapper ${snapshot.isDragging ? 'dragging' : ''}`}>
                          <span className="prioritization-modal__list__item__number">{index + 1}</span>
                          <div className={`prioritization-modal__list__item ${snapshot.isDragging ? 'dragging' : ''}`}>
                            <div className="prioritization-modal__list__item__drag-handle">
                              <i className="icon icon-hamburger" />
                            </div>
                            <div className="prioritization-modal__list__item__content">
                              <span className="prioritization-modal__list__item__name">{item.name}</span>
                              {renderItemDetails && (
                                <div className="prioritization-modal__list__item__right-content">
                                  {renderItemDetails(item, index)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <div className="prioritization-modal__actions">
        <Button
          onClick={handleClose}
          text={'Cancel'}
          size={'medium'}
          variant={'secondary'}
          className="prioritization-modal__actions__cancel"
        />
        <Button onClick={handleSave} text={'Save'} size={'medium'} variant={'primary'} disabled={isSaving} />
      </div>
    </Modal>
  );
}

export default PrioritizationModal;
